/**
 * EbmRozdeleniModal — editor for the EBM cell rules ("rozdělení buněk").
 *
 * Two kinds of rule move money between finished cells, both per report type and
 * applying to every month of that type:
 *
 *   - **podíl** — „F1 zůstává z 80 %, 20 % patří do H1". Only the receiving
 *     targets are entered; the source keeps the remainder, shown live. Ve výkazu
 *     se náklady a výnosy dělí zvlášť, rozvaha má jediné procento.
 *   - **pevná částka** — „z G převeď do B náklady ve výši výnosů buňky B", for a
 *     cell that must be balanced against another cell's value rather than split.
 *     The reference defaults to the target itself, which is the common case.
 *
 * Saving writes the whole set as the next version (`POST /ebm/rozdeleni`) —
 * a rule removed here is removed from the report.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { IconButton } from '@/components/IconButton';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import {
  useEbmRozdeleniQuery,
  useEbmSaveRozdeleniMutation,
  type EbmRozdeleniRadek,
  type EbmTyp,
} from './api';

interface TargetDraft {
  cil: string;
  /** Kept as strings so a half-typed „1" doesn't collapse to 1 %. */
  procentoN: string;
  procentoV: string;
}

interface GroupDraft {
  zdroj: string;
  targets: TargetDraft[];
}

/** „Z `zdroj` do `cil` převeď `sloupec` ve výši `odkazSloupec` buňky `odkazOzn`." */
interface TransferDraft {
  zdroj: string;
  cil: string;
  sloupec: 'N' | 'V';
  /** Empty = the target cell itself. */
  odkazOzn: string;
  odkazSloupec: 'N' | 'V';
}

const INPUT =
  'w-full border border-line bg-paper px-2 py-1 font-mono text-[12px] text-ink focus:border-accent focus:outline-none';
const SELECT =
  'border border-line bg-paper px-1.5 py-1 font-mono text-[12px] text-ink focus:border-accent focus:outline-none';

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    return (err.body as { error?: string }).error ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

/** Accepts both „12.5" and „12,5" — the keyboard people actually use. */
function pct(raw: string): number {
  const n = Number(raw.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function toGroups(rules: EbmRozdeleniRadek[]): GroupDraft[] {
  const byZdroj = new Map<string, GroupDraft>();
  for (const r of rules) {
    if (r.druh !== 'procento') continue;
    const g = byZdroj.get(r.zdroj) ?? { zdroj: r.zdroj, targets: [] };
    g.targets.push({ cil: r.cil, procentoN: String(r.procentoN), procentoV: String(r.procentoV) });
    byZdroj.set(r.zdroj, g);
  }
  return [...byZdroj.values()];
}

function toTransfers(rules: EbmRozdeleniRadek[]): TransferDraft[] {
  return rules
    .filter((r) => r.druh === 'castka')
    .map((r) => ({
      zdroj: r.zdroj,
      cil: r.cil,
      sloupec: r.sloupec === 'V' ? 'V' : 'N',
      // The server stores the resolved reference; show it as „(cíl)" when it is one.
      odkazOzn: r.odkazOzn === r.cil ? '' : r.odkazOzn,
      odkazSloupec: r.odkazSloupec === 'N' ? 'N' : 'V',
    }));
}

interface EbmRozdeleniModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  typ: EbmTyp;
  /** Cells present in the current report — offered as suggestions. */
  cells: string[];
}

export function EbmRozdeleniModal({ open, onOpenChange, typ, cells }: EbmRozdeleniModalProps) {
  const query = useEbmRozdeleniQuery(open, typ);
  const save = useEbmSaveRozdeleniMutation();
  const isVykaz = typ === 'vykaz';

  const [groups, setGroups] = useState<GroupDraft[]>([]);
  const [transfers, setTransfers] = useState<TransferDraft[]>([]);
  const [seeded, setSeeded] = useState(false);

  // Seed once per opening; re-seed on the next open so a cancelled edit is dropped.
  useEffect(() => {
    if (!open) {
      setSeeded(false);
      return;
    }
    if (!seeded && query.data) {
      setGroups(toGroups(query.data.rules));
      setTransfers(toTransfers(query.data.rules));
      setSeeded(true);
    }
  }, [open, seeded, query.data]);

  const patchGroup = (gi: number, patch: Partial<GroupDraft>) =>
    setGroups((prev) => prev.map((g, i) => (i === gi ? { ...g, ...patch } : g)));

  const patchTarget = (gi: number, ti: number, patch: Partial<TargetDraft>) =>
    setGroups((prev) =>
      prev.map((g, i) =>
        i === gi ? { ...g, targets: g.targets.map((t, j) => (j === ti ? { ...t, ...patch } : t)) } : g,
      ),
    );

  const addGroup = () =>
    setGroups((prev) => [...prev, { zdroj: '', targets: [{ cil: '', procentoN: '', procentoV: '' }] }]);

  const addTarget = (gi: number) =>
    setGroups((prev) =>
      prev.map((g, i) => (i === gi ? { ...g, targets: [...g.targets, { cil: '', procentoN: '', procentoV: '' }] } : g)),
    );

  const removeTarget = (gi: number, ti: number) =>
    setGroups((prev) =>
      prev.flatMap((g, i) => {
        if (i !== gi) return [g];
        const targets = g.targets.filter((_, j) => j !== ti);
        // A group without targets no longer says anything — drop it.
        return targets.length ? [{ ...g, targets }] : [];
      }),
    );

  const removeGroup = (gi: number) => setGroups((prev) => prev.filter((_, i) => i !== gi));

  const patchTransfer = (i: number, patch: Partial<TransferDraft>) =>
    setTransfers((prev) => prev.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  const addTransfer = () =>
    setTransfers((prev) => [
      ...prev,
      { zdroj: '', cil: '', sloupec: 'N', odkazOzn: '', odkazSloupec: 'V' },
    ]);

  const removeTransfer = (i: number) => setTransfers((prev) => prev.filter((_, j) => j !== i));

  /** Per-group remainders + the first blocking problem, recomputed as you type. */
  const state = useMemo(() => {
    const remainders = groups.map((g) => ({
      n: 100 - g.targets.reduce((s, t) => s + pct(t.procentoN), 0),
      v: 100 - g.targets.reduce((s, t) => s + pct(t.procentoV), 0),
    }));
    const seenZdroj = new Set<string>();
    let problem: string | null = null;
    groups.forEach((g, gi) => {
      const zdroj = g.zdroj.trim();
      if (!zdroj) problem ??= 'Vyplňte zdrojovou buňku u každého rozdělení.';
      else if (seenZdroj.has(zdroj)) problem ??= `Buňka ${zdroj} je uvedena dvakrát — slučte ji do jednoho rozdělení.`;
      else seenZdroj.add(zdroj);
      const seenCil = new Set<string>();
      for (const t of g.targets) {
        const cil = t.cil.trim();
        if (!cil) problem ??= `Vyplňte cílovou buňku u rozdělení ${zdroj || '—'}.`;
        else if (cil === zdroj) problem ??= `Buňka ${zdroj} nemůže být zdrojem i cílem.`;
        else if (seenCil.has(cil)) problem ??= `Cíl ${cil} je u ${zdroj} uveden dvakrát.`;
        else seenCil.add(cil);
        const values = isVykaz ? [pct(t.procentoN), pct(t.procentoV)] : [pct(t.procentoN)];
        if (values.some((v) => v < 0 || v > 100)) problem ??= `Procento u ${zdroj || '—'} musí být mezi 0 a 100.`;
      }
      const r = remainders[gi];
      if (r.n < 0) problem ??= `Buňka ${zdroj || '—'} rozděluje víc než 100 % ${isVykaz ? 'nákladů' : 'hodnoty'}.`;
      if (isVykaz && r.v < 0) problem ??= `Buňka ${zdroj || '—'} rozděluje víc než 100 % výnosů.`;
    });

    const seenTransfer = new Set<string>();
    for (const t of transfers) {
      const zdroj = t.zdroj.trim();
      const cil = t.cil.trim();
      if (!zdroj || !cil) problem ??= 'Vyplňte zdrojovou i cílovou buňku u každého převodu.';
      else if (zdroj === cil) problem ??= `Buňka ${zdroj} nemůže být zdrojem i cílem převodu.`;
      else {
        const key = `${zdroj}→${cil}·${isVykaz ? t.sloupec : ''}`;
        if (seenTransfer.has(key)) problem ??= `Převod ${zdroj} → ${cil} je uveden dvakrát.`;
        else seenTransfer.add(key);
      }
    }
    return { remainders, problem };
  }, [groups, transfers, isVykaz]);

  const targetCount = groups.reduce((s, g) => s + g.targets.length, 0);

  const submit = () => {
    if (state.problem) {
      toast.warn(state.problem);
      return;
    }
    const share: EbmRozdeleniRadek[] = groups.flatMap((g) =>
      g.targets.map((t) => ({
        zdroj: g.zdroj.trim(),
        cil: t.cil.trim(),
        druh: 'procento' as const,
        procentoN: pct(t.procentoN),
        // Rozvaha has a single value column — the V share is meaningless there.
        procentoV: isVykaz ? pct(t.procentoV) : 0,
        sloupec: '' as const,
        odkazOzn: '',
        odkazSloupec: '' as const,
      })),
    );
    const amount: EbmRozdeleniRadek[] = transfers.map((t) => ({
      zdroj: t.zdroj.trim(),
      cil: t.cil.trim(),
      druh: 'castka' as const,
      procentoN: 0,
      procentoV: 0,
      sloupec: isVykaz ? t.sloupec : ('' as const),
      // Empty means „the target itself"; the server resolves it.
      odkazOzn: t.odkazOzn.trim(),
      odkazSloupec: isVykaz ? t.odkazSloupec : ('' as const),
    }));
    save.mutate(
      { typ, rules: [...share, ...amount] },
      {
        onSuccess: (res) => {
          toast.ok(
            res.pocet > 0
              ? `Rozdělení uloženo — verze ${res.verze}, ${formatInt(res.pocet)} pravidel.`
              : `Rozdělení zrušeno — verze ${res.verze} je bez pravidel.`,
          );
          onOpenChange(false);
        },
        onError: (err) => toast.error(errMessage(err, 'Uložení rozdělení selhalo')),
      },
    );
  };

  const version = query.data?.version ?? null;

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={880}>
      <DialogCrown
        tone="neutral"
        eyebrow="Rozdělení buněk"
        eyebrowMeta={version ? <>verze {version.verze} · {version.vytvoreno.slice(0, 10)}</> : <>zatím bez pravidel</>}
        title={
          <>
            Přesouvat peníze mezi <em>buňkami</em>.
          </>
        }
        sub="Podílem, nebo pevnou částkou odvozenou z jiné buňky. Pravidla platí pro všechny měsíce tohoto typu."
      />
      <Modal.Body>
        <datalist id="ebm-rozdeleni-cells">
          {cells.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>

        <Modal.Section
          heading="Podíly"
          hint={
            isVykaz
              ? 'zadejte jen cíle — zbytek zůstává ve zdroji; náklady a výnosy zvlášť'
              : 'zadejte jen cíle — zbytek zůstává ve zdroji'
          }
        >
          {query.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : groups.length === 0 ? (
            <p className="text-[12px] leading-[1.6] text-ink-3">
              Zatím žádné rozdělení podílem — každá buňka se vykazuje celá.
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              {groups.map((g, gi) => {
                const rem = state.remainders[gi];
                return (
                  <div key={gi} className="border border-line bg-bg/40 p-4">
                    <div className="mb-3 flex items-center gap-2.5">
                      <label className="flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                        Zdroj
                        <input
                          value={g.zdroj}
                          onChange={(e) => patchGroup(gi, { zdroj: e.target.value })}
                          list="ebm-rozdeleni-cells"
                          placeholder={isVykaz ? 'např. F1' : 'např. L40'}
                          aria-label={`Zdrojová buňka rozdělení ${gi + 1}`}
                          className={INPUT + ' w-[120px]'}
                        />
                      </label>
                      <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                        zůstává{' '}
                        <b className={rem.n < 0 ? 'text-warn' : 'text-ink'}>
                          {rem.n.toFixed(rem.n % 1 === 0 ? 0 : 2)} %
                        </b>
                        {isVykaz && (
                          <>
                            {' '}
                            N ·{' '}
                            <b className={rem.v < 0 ? 'text-warn' : 'text-ink'}>
                              {rem.v.toFixed(rem.v % 1 === 0 ? 0 : 2)} %
                            </b>{' '}
                            V
                          </>
                        )}
                      </span>
                      <span className="flex-1" />
                      <IconButton
                        aria-label={`Odebrat rozdělení ${g.zdroj || gi + 1}`}
                        size="sm"
                        onClick={() => removeGroup(gi)}
                      >
                        <Icon name="close" size={12} />
                      </IconButton>
                    </div>

                    <table className="w-full border-collapse text-[12px]">
                      <thead>
                        <tr className="text-left font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                          <th className="pb-1.5 pr-3">Cíl</th>
                          <th className="w-[110px] pb-1.5 pr-3">{isVykaz ? '% nákladů' : '% hodnoty'}</th>
                          {isVykaz && <th className="w-[110px] pb-1.5 pr-3">% výnosů</th>}
                          <th className="w-[36px] pb-1.5" />
                        </tr>
                      </thead>
                      <tbody>
                        {g.targets.map((t, ti) => (
                          <tr key={ti}>
                            <td className="py-1 pr-3">
                              <input
                                value={t.cil}
                                onChange={(e) => patchTarget(gi, ti, { cil: e.target.value })}
                                list="ebm-rozdeleni-cells"
                                placeholder={isVykaz ? 'např. H1' : 'např. L45'}
                                aria-label={`Cílová buňka ${ti + 1} pro ${g.zdroj || 'rozdělení'}`}
                                className={INPUT}
                              />
                            </td>
                            <td className="py-1 pr-3">
                              <input
                                value={t.procentoN}
                                onChange={(e) => patchTarget(gi, ti, { procentoN: e.target.value })}
                                inputMode="decimal"
                                placeholder="0"
                                aria-label={`Procento ${isVykaz ? 'nákladů' : 'hodnoty'} pro ${t.cil || 'cíl'}`}
                                className={INPUT + ' text-right tabular-nums'}
                              />
                            </td>
                            {isVykaz && (
                              <td className="py-1 pr-3">
                                <input
                                  value={t.procentoV}
                                  onChange={(e) => patchTarget(gi, ti, { procentoV: e.target.value })}
                                  inputMode="decimal"
                                  placeholder="0"
                                  aria-label={`Procento výnosů pro ${t.cil || 'cíl'}`}
                                  className={INPUT + ' text-right tabular-nums'}
                                />
                              </td>
                            )}
                            <td className="py-1">
                              <IconButton
                                aria-label={`Odebrat cíl ${t.cil || ti + 1}`}
                                size="sm"
                                onClick={() => removeTarget(gi, ti)}
                              >
                                <Icon name="minus" size={12} />
                              </IconButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="mt-2">
                      <Button variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={() => addTarget(gi)}>
                        Přidat cíl
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4">
            <Button variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={addGroup}>
              Přidat rozdělení
            </Button>
          </div>
        </Modal.Section>

        <Modal.Section heading="Pevné částky" hint="částka se bere z hodnoty jiné buňky, ne z podílu">
          {transfers.length === 0 ? (
            <p className="text-[12px] leading-[1.6] text-ink-3">
              Zatím žádný převod. Hodí se, když má buňka vykázat protihodnotu k jiné buňce — třeba
              „B má 120 000 výnosů a musí mít i 120 000 nákladů, vezmi je z G".
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {transfers.map((t, i) => (
                <div
                  key={i}
                  className="flex flex-wrap items-center gap-1.5 border border-line bg-bg/40 p-3 text-[12px] text-ink-2"
                >
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Z</span>
                  <input
                    value={t.zdroj}
                    onChange={(e) => patchTransfer(i, { zdroj: e.target.value })}
                    list="ebm-rozdeleni-cells"
                    placeholder={isVykaz ? 'např. G1' : 'např. L40'}
                    aria-label={`Zdrojová buňka převodu ${i + 1}`}
                    className={INPUT + ' w-[100px]'}
                  />
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">do</span>
                  <input
                    value={t.cil}
                    onChange={(e) => patchTransfer(i, { cil: e.target.value })}
                    list="ebm-rozdeleni-cells"
                    placeholder={isVykaz ? 'např. B1' : 'např. L45'}
                    aria-label={`Cílová buňka převodu ${i + 1}`}
                    className={INPUT + ' w-[100px]'}
                  />
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">převést</span>
                  {isVykaz && (
                    <select
                      value={t.sloupec}
                      onChange={(e) => patchTransfer(i, { sloupec: e.target.value as 'N' | 'V' })}
                      aria-label={`Sloupec převodu ${i + 1}`}
                      className={SELECT}
                    >
                      <option value="N">náklady</option>
                      <option value="V">výnosy</option>
                    </select>
                  )}
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">ve výši</span>
                  {isVykaz && (
                    <select
                      value={t.odkazSloupec}
                      onChange={(e) => patchTransfer(i, { odkazSloupec: e.target.value as 'N' | 'V' })}
                      aria-label={`Sloupec, podle kterého se měří převod ${i + 1}`}
                      className={SELECT}
                    >
                      <option value="V">výnosů</option>
                      <option value="N">nákladů</option>
                    </select>
                  )}
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">buňky</span>
                  <input
                    value={t.odkazOzn}
                    onChange={(e) => patchTransfer(i, { odkazOzn: e.target.value })}
                    list="ebm-rozdeleni-cells"
                    placeholder={t.cil ? t.cil : 'cíl'}
                    aria-label={`Buňka, podle které se měří převod ${i + 1}`}
                    title="Prázdné = cílová buňka"
                    className={INPUT + ' w-[100px]'}
                  />
                  <span className="flex-1" />
                  <IconButton aria-label={`Odebrat převod ${i + 1}`} size="sm" onClick={() => removeTransfer(i)}>
                    <Icon name="close" size={12} />
                  </IconButton>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4">
            <Button variant="secondary" size="sm" icon={<Icon name="plus" size={12} />} onClick={addTransfer}>
              Přidat převod
            </Button>
          </div>
        </Modal.Section>
      </Modal.Body>
      <Modal.Foot
        meta={
          state.problem ? (
            <span className="text-warn">{state.problem}</span>
          ) : (
            <>
              <b>{formatInt(targetCount)}</b> podílů · <b>{formatInt(transfers.length)}</b> převodů · uložením
              vznikne nová verze
            </>
          )
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              Zrušit
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Icon name="check" size={12} />}
              onClick={submit}
              disabled={save.isPending || query.isLoading || state.problem !== null}
            >
              {save.isPending ? 'Ukládám…' : 'Uložit rozdělení'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}
